/**
 * API client for the Convention POS backend.
 *
 * Calls go to the Cloudflare Worker (same origin as the PWA in production, or
 * the `/api` dev proxy locally), which forwards them to AWS.
 *
 * Two things here are load-bearing for a convention:
 *
 * - **Token refresh is transparent and de-duplicated.** The access token lives
 *   15 minutes; when it expires mid-transaction the client refreshes once and
 *   replays the request. Concurrent 401s share a single refresh so a burst of
 *   parallel calls cannot trigger a refresh storm (and, because refresh tokens
 *   rotate, a storm would invalidate the session outright).
 * - **The access token is kept in memory only.** It is never written to
 *   `localStorage`, so a stored token cannot be read by injected script. The
 *   `HttpOnly` refresh cookie is what survives a reload.
 */

// Always /api: in production a Worker route on the site's own hostname serves
// /api/*, and in development Vite proxies it. Same origin either way, so no
// CORS and a first-party refresh cookie.
export const API_BASE = '/api';

/** A user as the backend describes them. */
export interface User {
  email: string;
  name: string;
  role: string;
  permissions: string[];
}

/**
 * An inventory item. Mirrors the desktop app's item shape.
 *
 * Note the two distinct codes:
 * - `item_id` is the app's internal id — the key every API call takes, and
 *   what the app's own printed barcodes encode.
 * - `upc` is the *manufacturer's* printed barcode, typed in by the user. It
 *   may be blank and is not unique, so never use it to address an item.
 */
export interface Item {
  item_id?: string;
  upc: string;
  name: string;
  price: number;
  count: number;
  rank?: string | number;
  purchase_cost?: number;
  sold?: number;
  tags?: string[];
  payout_to?: Record<string, number>;
  sell_date?: Record<string, unknown>;
  sell_total?: number;
  infinite?: boolean;
  variants?: Variant[];
}

/** A grab-bag variant: an extra barcode on one item (issue I-39). */
export interface Variant {
  upc: string;
  label: string;
  count: number;
  price: number | null;
}

/** A line on an open order. */
export interface OrderLine {
  upc: string;
  name: string;
  count: number;
  price: number;
  db_path: string;
  variant_upc?: string | null;
}

/** An error carrying the HTTP status, so callers can branch on it. */
export class ApiError extends Error {
  readonly status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }

  /** True when the failure looks like "no usable network", not a real refusal. */
  get isOffline(): boolean {
    return this.status === 0 || this.status === 503;
  }
}

let accessToken: string | null = null;
let onUnauthenticated: (() => void) | null = null;
/** Shared in-flight refresh, so parallel 401s trigger exactly one refresh. */
let refreshInFlight: Promise<boolean> | null = null;

/** Store the access token in memory. */
export function setAccessToken(token: string | null): void {
  accessToken = token;
}

/** Whether a token is currently held. */
export function hasAccessToken(): boolean {
  return accessToken !== null;
}

/** Register a callback invoked when the session cannot be recovered. */
export function setUnauthenticatedHandler(handler: (() => void) | null): void {
  onUnauthenticated = handler;
}

/**
 * Exchange the refresh cookie for a new access token.
 *
 * @returns true when a new token was obtained.
 */
async function refreshAccessToken(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight;

  refreshInFlight = (async () => {
    try {
      const response = await fetch(`${API_BASE}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!response.ok) return false;
      const data = (await response.json()) as { access_token?: string };
      if (!data.access_token) return false;
      accessToken = data.access_token;
      return true;
    } catch {
      return false;
    } finally {
      // Cleared in a microtask so callers awaiting this promise all observe
      // the same result before a new refresh can start.
      queueMicrotask(() => {
        refreshInFlight = null;
      });
    }
  })();

  return refreshInFlight;
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  /** Set false to skip the refresh-and-retry path (used by refresh itself). */
  retry?: boolean;
}

/**
 * Make an authenticated API request.
 *
 * @param path API path, e.g. `/items?db=databases/db.json`.
 * @param options Method, JSON body, and retry behaviour.
 * @throws {ApiError} On any non-2xx response or network failure.
 */
export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, retry = true } = options;

  const headers: Record<string, string> = {};
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
  if (body !== undefined) headers['Content-Type'] = 'application/json';

  let response: Response;
  try {
    response = await fetch(`${API_BASE}${path}`, {
      method,
      headers,
      credentials: 'include',
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch (error) {
    // fetch only rejects on a network-level failure, which is exactly the
    // "convention WiFi died" case the caller needs to distinguish.
    throw new ApiError((error as Error).message || 'Network unavailable', 0);
  }

  if (response.status === 401 && retry) {
    if (await refreshAccessToken()) {
      return request<T>(path, { ...options, retry: false });
    }
    accessToken = null;
    onUnauthenticated?.();
    throw new ApiError('Your session has expired. Sign in again.', 401);
  }

  if (response.status === 204) return undefined as T;

  let payload: unknown = null;
  const text = await response.text();
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = { error: text };
    }
  }

  if (!response.ok) {
    const message =
      (payload as { error?: string } | null)?.error ??
      `Request failed (${response.status})`;
    throw new ApiError(message, response.status);
  }

  return payload as T;
}

// --------------------------------------------------------------------------
// Auth
// --------------------------------------------------------------------------

/** Sign in and store the returned access token. */
export async function login(email: string, password: string): Promise<User> {
  const data = await request<{ access_token: string; user: User }>('/auth/login', {
    method: 'POST',
    body: { email, password },
    retry: false,
  });
  accessToken = data.access_token;
  return data.user;
}

/** Create the first (bootstrap) admin account and sign in. */
export async function registerFirstAdmin(
  email: string,
  password: string,
  name: string,
): Promise<User> {
  const data = await request<{ access_token: string; user: User }>('/auth/register', {
    method: 'POST',
    body: { email, password, name },
    retry: false,
  });
  if (data.access_token) accessToken = data.access_token;
  return data.user;
}

/** Restore a session from the refresh cookie on page load. */
export async function restoreSession(): Promise<User | null> {
  if (!(await refreshAccessToken())) return null;
  try {
    return await request<User>('/auth/me', { retry: false });
  } catch {
    return null;
  }
}

/** Sign out and clear the refresh cookie. */
export async function logout(): Promise<void> {
  try {
    await request('/auth/logout', { method: 'POST', retry: false });
  } finally {
    accessToken = null;
  }
}

// --------------------------------------------------------------------------
// Inventory
// --------------------------------------------------------------------------

/** List the inventory databases. */
export function listDatabases(): Promise<{ databases: { db_path: string }[] }> {
  return request('/databases');
}

/** Fetch every item in a database. */
export function listItems(db: string): Promise<{ items: Record<string, Item> }> {
  return request(`/items?db=${encodeURIComponent(db)}`);
}

/** Look up one code, resolving a grab-bag variant to its owning item. */
export function getItem(
  upc: string,
  db: string,
): Promise<{ upc: string; item: Item; variant: Variant | null }> {
  return request(`/items/${encodeURIComponent(upc)}?db=${encodeURIComponent(db)}`);
}

/** Create an item. */
export function createItem(db: string, item: Partial<Item>): Promise<{ upc: string; item: Item }> {
  return request('/items', { method: 'POST', body: { db_path: db, item } });
}

/** Replace an item. */
export function updateItem(db: string, upc: string, item: Partial<Item>): Promise<unknown> {
  return request(`/items/${encodeURIComponent(upc)}`, {
    method: 'PUT',
    body: { db_path: db, item },
  });
}

/** Delete an item. */
export function deleteItem(db: string, upc: string): Promise<unknown> {
  return request(`/items/${encodeURIComponent(upc)}?db=${encodeURIComponent(db)}`, {
    method: 'DELETE',
  });
}

/** Record a direct sale (no order). */
export function sellItem(
  db: string,
  upc: string,
  payload: { count: number; unit_price?: number; payment_method?: string; variant_upc?: string | null },
): Promise<unknown> {
  return request(`/items/${encodeURIComponent(upc)}/sell`, {
    method: 'POST',
    body: { db_path: db, ...payload },
  });
}

/** Add a grab-bag variant barcode to an item. */
export function createVariant(
  db: string,
  upc: string,
  payload: { label: string; count: number; price?: number | null },
): Promise<{ variant: Variant }> {
  return request(`/items/${encodeURIComponent(upc)}/variants`, {
    method: 'POST',
    body: { db_path: db, ...payload },
  });
}

/** Remove a grab-bag variant. */
export function deleteVariant(db: string, upc: string, variantUpc: string): Promise<unknown> {
  return request(
    `/items/${encodeURIComponent(upc)}/variants/${encodeURIComponent(variantUpc)}` +
      `?db=${encodeURIComponent(db)}`,
    { method: 'DELETE' },
  );
}

// --------------------------------------------------------------------------
// Orders
// --------------------------------------------------------------------------

/** List open orders. */
export function listOrders(): Promise<{
  orders: { name: string; lines: number; total: number }[];
}> {
  return request('/orders');
}

/** Create an empty order. */
export function createOrder(name: string): Promise<unknown> {
  return request('/orders', { method: 'POST', body: { name } });
}

/** Fetch an order's lines. */
export function getOrder(
  name: string,
): Promise<{ name: string; items: Record<string, OrderLine>; total: number }> {
  return request(`/orders/${encodeURIComponent(name)}`);
}

/** Add a line to an order. */
export function addOrderItem(
  name: string,
  payload: { db_path: string; upc: string; count: number; price?: number; variant_upc?: string | null },
): Promise<unknown> {
  return request(`/orders/${encodeURIComponent(name)}/items`, {
    method: 'POST',
    body: payload,
  });
}

/** Change a line's quantity or price. */
export function updateOrderItem(
  name: string,
  upc: string,
  payload: { count?: number; price?: number },
): Promise<unknown> {
  return request(
    `/orders/${encodeURIComponent(name)}/items/${encodeURIComponent(upc)}`,
    { method: 'PATCH', body: payload },
  );
}

/** Remove a line from an order. */
export function deleteOrderItem(name: string, upc: string): Promise<unknown> {
  return request(
    `/orders/${encodeURIComponent(name)}/items/${encodeURIComponent(upc)}`,
    { method: 'DELETE' },
  );
}

/** Delete an order. */
export function deleteOrder(name: string): Promise<unknown> {
  return request(`/orders/${encodeURIComponent(name)}`, { method: 'DELETE' });
}

/** Apply a percentage discount to an order. */
export function discountOrder(name: string, percent: number): Promise<unknown> {
  return request(`/orders/${encodeURIComponent(name)}/discount`, {
    method: 'POST',
    body: { percent },
  });
}

/** Set an order to an exact total (issue I-49). */
export function setOrderTotal(name: string, total: number): Promise<unknown> {
  return request(`/orders/${encodeURIComponent(name)}/total`, {
    method: 'POST',
    body: { total },
  });
}

/** Submit an order: sell every line and record the sale. */
export function submitOrder(
  name: string,
  payload: { payment_method?: string; customer?: string; allow_overdraw?: boolean },
): Promise<{
  sale_id: number;
  total: number;
  sold: string[];
  problems: string[];
  /** True when some lines could not be sold, so the order was kept. */
  order_kept: boolean;
  credit_balance: number | null;
}> {
  return request(`/orders/${encodeURIComponent(name)}/submit`, {
    method: 'POST',
    body: payload,
  });
}

// --------------------------------------------------------------------------
// Store credit
// --------------------------------------------------------------------------

/** Read a customer's store-credit balance and history. */
export function getCredit(customer: string): Promise<{
  customer: string;
  balance: number;
  entries: { amount: number; reason: string; timestamp: string; reference: string }[];
}> {
  return request(`/credit/${encodeURIComponent(customer)}`);
}

/** Grant or deduct store credit. */
export function postCredit(payload: {
  customer: string;
  amount: number;
  reason?: string;
  allow_overdraw?: boolean;
}): Promise<{ balance: number }> {
  return request('/credit', { method: 'POST', body: payload });
}

// --------------------------------------------------------------------------
// Payee contacts and payouts
// --------------------------------------------------------------------------

/** A payee: who they are, how they want to be paid, how to reach them. */
export interface Contact {
  key?: string;
  name: string;
  kind: 'individual' | 'business';
  payment_method: string;
  payment_handle: string;
  email: string;
  phone: string;
  address: string;
  notes: string;
}

/** A line on a payout statement: what one payee is owed. */
export interface PayoutRow extends Contact {
  payee: string;
  revenue: number;
  share: number;
  units: number;
  sales: number;
  has_contact: boolean;
}

/** Payment methods offered, matching the desktop app's list. */
export const PAYMENT_METHODS: [string, string][] = [
  ['check', 'Check'],
  ['cash', 'Cash'],
  ['venmo', 'Venmo'],
  ['paypal', 'PayPal'],
  ['zelle', 'Zelle'],
  ['cashapp', 'Cash App'],
  ['wire', 'Wire transfer'],
  ['store_credit', 'Store credit'],
  ['other', 'Other'],
];

/** List every payee contact. */
export function listContacts(): Promise<{ contacts: Contact[] }> {
  return request('/contacts');
}

/** Create or update a payee contact. */
export function saveContact(
  payload: Partial<Contact> & { name: string; original_name?: string },
): Promise<Contact> {
  return request('/contacts', { method: 'POST', body: payload });
}

/** Delete a payee contact. Payout history keeps the name. */
export function deleteContact(name: string): Promise<unknown> {
  return request(`/contacts/${encodeURIComponent(name)}`, { method: 'DELETE' });
}

/** What each payee is owed, optionally bounded by date. */
export function payoutStatement(
  db: string,
  range: { since?: string; until?: string } = {},
): Promise<{ total: number; payees: PayoutRow[] }> {
  const query = new URLSearchParams({ db });
  if (range.since) query.set('since', range.since);
  if (range.until) query.set('until', range.until);
  return request(`/payouts?${query.toString()}`);
}

// --------------------------------------------------------------------------
// eBay
// --------------------------------------------------------------------------

/** eBay connection status. */
export function ebayStatus(): Promise<{
  env: string;
  configured: boolean;
  connected: boolean;
  connected_at: string;
  marketplace_id: string;
}> {
  return request('/ebay/status');
}

/** Get the eBay consent URL. */
export function ebayAuthorizeUrl(): Promise<{ url: string }> {
  return request('/ebay/authorize');
}

/** Complete the eBay connection with the redirect code. */
export function ebayConnect(code: string): Promise<unknown> {
  return request('/ebay/connect', { method: 'POST', body: { code } });
}

/** Disconnect the eBay account. */
export function ebayDisconnect(): Promise<unknown> {
  return request('/ebay/disconnect', { method: 'POST' });
}

/** Fetch the seller's business policies and inventory locations. */
export function ebayPolicies(): Promise<{
  fulfillment: { id: string; name: string }[];
  payment: { id: string; name: string }[];
  return: { id: string; name: string }[];
  locations: { key: string; name: string }[];
}> {
  return request('/ebay/policies');
}

/** List the item-to-listing links for a database. */
export function ebayLinks(db: string): Promise<{ links: Record<string, Record<string, unknown>> }> {
  return request(`/ebay/links?db=${encodeURIComponent(db)}`);
}

/** Publish an item to eBay. */
export function ebayPublish(
  db: string,
  upc: string,
  payload: Record<string, unknown>,
): Promise<unknown> {
  return request(`/ebay/items/${encodeURIComponent(upc)}/publish`, {
    method: 'POST',
    body: { db_path: db, ...payload },
  });
}

/** Push an item's current stock to its eBay listing (issue I-11). */
export function ebaySync(db: string, upc: string): Promise<unknown> {
  return request(`/ebay/items/${encodeURIComponent(upc)}/sync`, {
    method: 'POST',
    body: { db_path: db },
  });
}

/** End an item's eBay listing. */
export function ebayEnd(db: string, upc: string): Promise<unknown> {
  return request(`/ebay/items/${encodeURIComponent(upc)}/end`, {
    method: 'POST',
    body: { db_path: db },
  });
}

// --------------------------------------------------------------------------
// Users
// --------------------------------------------------------------------------

/** List accounts (admin only). */
export function listUsers(): Promise<{ users: User[] }> {
  return request('/users');
}

/** Create an account (admin only). */
export function createUser(payload: {
  email: string;
  password: string;
  role: string;
  name: string;
}): Promise<unknown> {
  return request('/auth/register', { method: 'POST', body: payload });
}

/** Change an account's role, name or password (admin only). */
export function updateUser(
  email: string,
  payload: { role?: string; name?: string; password?: string },
): Promise<unknown> {
  return request(`/users/${encodeURIComponent(email)}`, { method: 'PATCH', body: payload });
}

/** Delete an account (admin only). */
export function deleteUser(email: string): Promise<unknown> {
  return request(`/users/${encodeURIComponent(email)}`, { method: 'DELETE' });
}
